import styles from './CheckCoverageDialog.module.scss';

import * as React from 'react';
import * as ReactDOM from 'react-dom';

import { Dialog, DialogType, Label, TextField, KeyCodes, Separator } from '@fluentui/react';

import { HealAPI } from '../../healApi';
import { toastr } from 'react-redux-toastr';
import { AddressInput, SuggestAddress } from '../core/AddressInput';

export interface CheckCoverageDialogProps {}

export interface CheckCoverageDialogState {
  value: string;
  suggest?: SuggestAddress;
  zipcode?: string;
}

export class CheckCoverageDialog {
  public static show(options?: CheckCoverageDialogProps) {
    const dialogNode = document.createElement('div');
    const close = () => {
      ReactDOM.unmountComponentAtNode(dialogNode);
      dialogNode.remove();
    };
    ReactDOM.render(<CheckCoverageDialogInternal {...options} close={close} />, dialogNode);
  }
}

class CheckCoverageDialogInternal extends React.Component<CheckCoverageDialogProps & { close: () => void }, CheckCoverageDialogState> {
  constructor(props: any) {
    super(props);
    this.state = {
      value: '',
    };
  }

  public render(): JSX.Element {
    return (
      <Dialog
        modalProps={{
          className: styles.checkCoverageDialog,
        }}
        hidden={false}
        dialogContentProps={{
          type: DialogType.largeHeader,
          title: 'Check Coverage',
        }}
        onDismiss={this.props.close}
      >
        <Label>Zipcode (Press Enter to search)</Label>
        <TextField onChange={(ev, value) => this.setState({ zipcode: value! })} onKeyDown={this.onKeyDown} value={this.state.zipcode} validateOnFocusOut={true} />
        <Separator>OR</Separator>
        <Label>Address</Label>
        <AddressInput onSelect={this.onAddressSelect} />
      </Dialog>
    );
  }

  private onAddressSelect = (suggest?: SuggestAddress) => {
    this.setState({ suggest }, this.fireChange);
  };

  // Zipcode <zipcode> is fully covered for HOUSE and TELEHEALTH services. Market is <marketname> (<marketcode>).
  // Zipcode <zipcode> is fully covered for TELEHEALTH services. Market is <marketname> (<marketcode>).
  // Zipcode <zipcode> is partially covered for HOUSE and TELEHEALTH services. Market is <marketname> (<marketcode>).
  // Zipcode <zipcode> is not in a Heal coverage area.
  private onKeyDown = (e: React.KeyboardEvent<any>) => {
    const { zipcode } = this.state;
    if (e.keyCode === KeyCodes.enter && zipcode) {
      if (zipcode.length !== 5 || isNaN(zipcode as any)) {
        toastr.warning('Issue', 'You must enter a valid zip code that is 5 digits.');
      } else {
        HealAPI.checkZipcodeCoverage(zipcode).then((response) => {
          const { covered, supportHouseCallServices, marketName, marketCode, serviceabilityDelayInSeconds, partialZipcodeCoverage } = response.data;
          if (!covered) {
            toastr.error('Not Covered', `Zipcode ${zipcode} is not in a Heal coverage area.`);
          } else {
            const message = `Zipcode ${zipcode} is ${partialZipcodeCoverage ? 'partially' : 'fully'} covered for ${
              supportHouseCallServices ? 'HOUSE and TELEHEALTH' : 'TELEHEALTH'
            } services. Market is ${marketName} (${marketCode}).`;
            const minBooking = serviceabilityDelayInSeconds === 0 ? 'SAME DAY' : `${serviceabilityDelayInSeconds / (60 * 60 * 24)} HOURS`;
            if (partialZipcodeCoverage) {
              toastr.warning(`Partial Coverage (${minBooking})`, message);
            } else {
              toastr.success(`Covered (${minBooking})`, message);
            }
          }
        });
      }
    }
  };

  // Address is covered for HOUSE and TELEHEALTH services. Market is <marketname> (<marketcode>).
  // Address is covered for TELEHEALTH services. Market is <marketname> (<marketcode>).
  // Address is not in a Heal coverage area.
  private fireChange() {
    if (this.state.suggest) {
      HealAPI.checkAddressCoverage(this.state.suggest.latitude, this.state.suggest.longitude).then((response) => {
        const { covered, supportHouseCallServices, marketName, marketCode, serviceabilityDelayInSeconds } = response.data;
        if (!covered) {
          toastr.error('Not Covered', `Address is not in a Heal coverage area.`);
        } else {
          const message = `Address is covered for ${supportHouseCallServices ? 'HOUSE and TELEHEALTH' : 'TELEHEALTH'} services. Market is ${marketName} (${marketCode}).`;
          const minBooking = serviceabilityDelayInSeconds === 0 ? 'SAME DAY' : `${serviceabilityDelayInSeconds / (60 * 60 * 24)} hours`;
          toastr.success(`Covered (${minBooking})`, message);
        }
      });
    }
  }
}
